// DM's excluded-teeth notifications, grouped the way DM's own editor groups them. DM's API
// only lists the references; the grouping and labels are ours.

export interface ExcludedTeethObservation {
    id: string;
    label: string;
    references: readonly string[];
}

export interface ExcludedTeethCategory {
    id: string;
    label: string;
    observations: readonly ExcludedTeethObservation[];
}

/** References that also come as a "<reference>_still" follow-up notification. */
const HAS_STILL = new Set([
    'aligner_attachment_loss',
    'aligner_button_debonding',
    'archwire_poking_distally',
    'black_triangle',
    'brace_appliance_damage',
    'brace_archwire_disengagement',
    'brace_archwire_distortion',
    'brace_archwire_fracture',
    'brace_archwire_hook_missing',
    'brace_bite_opening_composite_missing',
    'brace_bracket_damage',
    'brace_bracket_debonding',
    'brace_button_debonding',
    'brace_occlusal_interference',
    'brace_passive_archwire',
    'brace_powerchain_damage',
    'brace_self_ligating_clip_open',
    'brace_spring_damage',
    'brace_tad_missing',
    'brace_tie_loss',
    'cavity',
    'crossbite_development',
    'debonding_dental_restoration',
    'dental_calculus',
    'ectopic_eruption',
    'edge_to_edge_crossbite',
    'embedding_appliance',
    'fixed_retainer_damage',
    'gingival_recession',
    'insufficient_cleaning_appliance',
    'midline_deviation_developing',
    'mild_diastema',
    'mucosa_irregularity',
    'noticeable_aligner_unseat',
    'noticeable_diastema',
    'noticeable_gingivitis',
    'noticeable_hygiene_insufficiency',
    'removable_retainer_damage',
    'removable_retainer_deterioration',
    'satisfactory_hygiene',
    'sign_of_infection',
    'slight_aligner_unseat',
    'slight_gingival_recession',
    'slight_gingivitis',
    'slight_hygiene_insufficiency',
    'slight_removable_retainer_unseat',
    'slight_tooth_abrasion',
    'spots',
    'submerged_tooth',
    'tooth_abrasion',
    'tooth_eruption_crossbite',
    'tooth_fracture'
]);

const STILL_SUFFIX = '_still';

function refs(...base: string[]): string[] {
    return base.flatMap((ref) => (HAS_STILL.has(ref) ? [ref, `${ref}${STILL_SUFFIX}`] : [ref]));
}

function observation(id: string, label: string, references: string[]): ExcludedTeethObservation {
    return { id, label, references };
}

const GOALS = [
    'all_deciduous_teeth_lost',
    'anterior_spacing_closed',
    'class_i_cuspid_left',
    'class_i_cuspid_right',
    'class_i_molar_left',
    'class_i_molar_right',
    'crossbite_corrected_left',
    'crossbite_corrected_right',
    'eruption_advanced',
    'expansion',
    'extraction_spacing_closed',
    'midline_aligned',
    'no_more_movement_md',
    'no_more_movement_mx',
    'normal_overbite',
    'normal_overjet',
    'opening_closing_spaces',
    'other_custom_measure'
].map((goal) => `goal_achieved_${goal}`);

export const EXCLUDED_TEETH_TAXONOMY: readonly ExcludedTeethCategory[] = [
    {
        id: 'goals',
        label: 'Goals',
        observations: [observation('goals-general', 'General', GOALS)]
    },
    {
        id: 'aligners',
        label: 'Orthodontic parameters, aligners',
        observations: [
            observation(
                'aligner-tracking',
                'Tracking',
                refs(
                    'aligner_first_scan_welcome_message',
                    'satisfactory_aligner_tracking',
                    'slight_aligner_unseat',
                    'noticeable_aligner_unseat'
                )
            ),
            observation(
                'aligner-attachment-loss',
                'Attachment loss',
                refs('aligner_attachment_loss')
            ),
            observation(
                'aligner-auxiliary',
                'Auxiliary appliance',
                refs(
                    'aligner_auxiliary_damage',
                    'aligner_auxiliary_debonding',
                    'aligner_button_debonding'
                )
            ),
            observation(
                'aligner-damage',
                'Aligner damage',
                refs('aligner_abrasion', 'aligner_damage', 'aligner_distortion', 'aligner_fracture')
            )
        ]
    },
    {
        id: 'braces',
        label: 'Orthodontic parameters, braces',
        observations: [
            observation(
                'braces-condition',
                'Brackets and attachments',
                refs(
                    'satisfactory_braces_condition',
                    'brace_appliance_damage',
                    'brace_bracket_damage',
                    'brace_bracket_debonding',
                    'brace_button_debonding',
                    'brace_tie_loss',
                    'brace_self_ligating_clip_open',
                    'brace_bite_opening_composite_missing'
                )
            ),
            observation(
                'braces-archwire',
                'Archwire',
                refs(
                    'brace_archwire_disengagement',
                    'brace_archwire_distortion',
                    'brace_archwire_fracture',
                    'brace_archwire_hook_missing',
                    'brace_passive_archwire',
                    'archwire_poking_distally'
                )
            ),
            observation(
                'braces-auxiliaries',
                'Auxiliaries',
                refs(
                    'brace_powerchain_damage',
                    'brace_spring_damage',
                    'brace_tad_missing',
                    'brace_occlusal_interference'
                )
            )
        ]
    },
    {
        id: 'oral-health',
        label: 'Oral health assessment',
        observations: [
            observation(
                'oral-hygiene',
                'Hygiene',
                refs(
                    'satisfactory_hygiene',
                    'slight_hygiene_insufficiency',
                    'noticeable_hygiene_insufficiency',
                    'insufficient_cleaning_appliance',
                    'dental_calculus'
                )
            ),
            observation(
                'oral-gingiva',
                'Gums and soft tissue',
                refs(
                    'slight_gingivitis',
                    'noticeable_gingivitis',
                    'slight_gingival_recession',
                    'gingival_recession',
                    'black_triangle',
                    'embedding_appliance',
                    'mucosa_irregularity',
                    'sign_of_infection'
                )
            ),
            observation(
                'oral-teeth',
                'Teeth',
                refs(
                    'cavity',
                    'spots',
                    'tooth_color_change',
                    'slight_tooth_abrasion',
                    'tooth_abrasion',
                    'tooth_fracture',
                    'tooth_loss',
                    'debonding_dental_restoration'
                )
            )
        ]
    },
    {
        id: 'intraoral',
        label: 'Intraoral evaluation',
        observations: [
            observation(
                'intraoral-spacing',
                'Spacing',
                refs('mild_diastema', 'noticeable_diastema')
            ),
            observation(
                'intraoral-occlusion',
                'Occlusion',
                refs(
                    'crossbite_development',
                    'edge_to_edge_crossbite',
                    'midline_deviation_developing'
                )
            ),
            observation(
                'intraoral-eruption',
                'Eruption',
                refs(
                    'eruption_started',
                    'eruption_advanced',
                    'ectopic_eruption',
                    'submerged_tooth',
                    'tooth_eruption_crossbite',
                    'tooth_eruption_rotation',
                    'all_deciduous_teeth_lost'
                )
            )
        ]
    },
    {
        id: 'retention',
        label: 'Retention',
        observations: [
            observation(
                'retention-tracking',
                'Removable retainer tracking',
                refs(
                    'satisfactory_removable_retainer_fit',
                    'slight_removable_retainer_unseat',
                    'noticeable_removable_retainer_unseat'
                )
            ),
            observation(
                'retention-damage',
                'Retainer damage',
                refs(
                    'removable_retainer_damage',
                    'removable_retainer_deterioration',
                    'fixed_retainer_damage'
                )
            ),
            observation(
                'retention-relapse',
                'Relapse',
                refs(
                    'satisfactory_stability',
                    'slight_movement',
                    'alignment_relapse',
                    'occlusion_relapse'
                )
            )
        ]
    }
];

const LABEL_OVERRIDES: Record<string, string> = {
    aligner_first_scan_welcome_message: 'First scan, welcome message',
    slight_aligner_unseat: 'Slight unseat',
    noticeable_aligner_unseat: 'Noticeable unseat',
    aligner_attachment_loss: 'Attachment loss',
    aligner_auxiliary_damage: 'Auxiliary damage',
    aligner_auxiliary_debonding: 'Auxiliary debonding',
    aligner_button_debonding: 'Button debonding',
    brace_tad_missing: 'TAD missing',
    brace_self_ligating_clip_open: 'Self-ligating clip open',
    goal_achieved_class_i_cuspid_left: 'Class I cuspid, left',
    goal_achieved_class_i_cuspid_right: 'Class I cuspid, right',
    goal_achieved_class_i_molar_left: 'Class I molar, left',
    goal_achieved_class_i_molar_right: 'Class I molar, right',
    goal_achieved_crossbite_corrected_left: 'Crossbite corrected, left',
    goal_achieved_crossbite_corrected_right: 'Crossbite corrected, right',
    goal_achieved_no_more_movement_md: 'No more movement, lower',
    goal_achieved_no_more_movement_mx: 'No more movement, upper',
    goal_achieved_opening_closing_spaces: 'Opening or closing spaces'
};

function humanize(reference: string): string {
    const words = reference.replace(/^(goal_achieved|brace)_/, '').replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

export function notificationLabel(reference: string): string {
    if (reference.endsWith(STILL_SUFFIX)) {
        return `${notificationLabel(reference.slice(0, -STILL_SUFFIX.length))} still present`;
    }
    return LABEL_OVERRIDES[reference] ?? humanize(reference);
}

const KNOWN_REFERENCES = new Set(
    EXCLUDED_TEETH_TAXONOMY.flatMap((category) =>
        category.observations.flatMap((entry) => entry.references)
    )
);

/** Adds an "Other" group for references DM returns that the taxonomy doesn't know yet. */
export function taxonomyWith(references: Iterable<string>): readonly ExcludedTeethCategory[] {
    const unknown = [...references].filter((reference) => !KNOWN_REFERENCES.has(reference));
    if (unknown.length === 0) return EXCLUDED_TEETH_TAXONOMY;
    return [
        ...EXCLUDED_TEETH_TAXONOMY,
        {
            id: 'other',
            label: 'Other',
            observations: [observation('other', 'Other', unknown.sort())]
        }
    ];
}

/** One notification with where it sits in the taxonomy. */
export interface NotificationEntry {
    reference: string;
    category: ExcludedTeethCategory;
    observation: ExcludedTeethObservation;
}

export function notificationEntries(
    categories: readonly ExcludedTeethCategory[]
): NotificationEntry[] {
    return categories.flatMap((category) =>
        category.observations.flatMap((entry) =>
            entry.references.map((reference) => ({ reference, category, observation: entry }))
        )
    );
}
